/* =========================================================================
   Lozea — 3D planet + pinned-scroll parallax intro
   Vanilla port of the React/Framer-Motion version. Three.js is pulled from
   a CDN (jsDelivr). One instance per `.lozea-hero` section on the page.
   ========================================================================= */

import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';

const TURNS = Math.PI * 4;      // full rotation sweep across the pinned scroll
const SCALE_FROM = 0.85;
const SCALE_TO = 1.0;

const planetVertex = `
  varying vec3 vLocalPos;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  void main() {
    vLocalPos = normalize(position);
    vNormal = normalize(normalMatrix * normal);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewDir = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const planetFragment = `
  precision highp float;
  varying vec3 vLocalPos;
  varying vec3 vNormal;
  varying vec3 vViewDir;

  uniform vec3 uLightDir;
  uniform vec3 uShadow;
  uniform vec3 uBase;
  uniform vec3 uLight;
  uniform vec3 uRim;

  vec3 hash3(vec3 p) {
    p = vec3(dot(p, vec3(127.1, 311.7, 74.7)),
             dot(p, vec3(269.5, 183.3, 246.1)),
             dot(p, vec3(113.5, 271.9, 124.6)));
    return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
  }

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(dot(hash3(i + vec3(0,0,0)), f - vec3(0,0,0)),
                       dot(hash3(i + vec3(1,0,0)), f - vec3(1,0,0)), u.x),
                   mix(dot(hash3(i + vec3(0,1,0)), f - vec3(0,1,0)),
                       dot(hash3(i + vec3(1,1,0)), f - vec3(1,1,0)), u.x), u.y),
               mix(mix(dot(hash3(i + vec3(0,0,1)), f - vec3(0,0,1)),
                       dot(hash3(i + vec3(1,0,1)), f - vec3(1,0,1)), u.x),
                   mix(dot(hash3(i + vec3(0,1,1)), f - vec3(0,1,1)),
                       dot(hash3(i + vec3(1,1,1)), f - vec3(1,1,1)), u.x), u.y), u.z);
  }

  float fbm(vec3 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 6; i++) {
      v += a * noise(p);
      p *= 2.02;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 p = vLocalPos;
    vec3 w = vec3(fbm(p * 1.6), fbm(p * 1.6 + 11.3), fbm(p * 1.6 + 7.1));
    float clouds = fbm(p * 2.4 + w * 1.35) * 0.5 + 0.5;
    float bands = sin(p.y * 4.3 + (clouds - 0.5) * 6.0) * 0.5 + 0.5;
    float tex = mix(clouds, bands, 0.42);
    tex = smoothstep(0.12, 0.92, tex);

    vec3 col = mix(uShadow, uBase, tex);
    col = mix(col, uLight, smoothstep(0.58, 1.0, tex) * 0.65);

    vec3 N = normalize(vNormal);
    float lambert = dot(N, normalize(uLightDir));
    float lit = smoothstep(-0.45, 0.75, lambert);
    col *= mix(0.34, 1.13, lit);

    float fres = pow(1.0 - max(dot(N, normalize(vViewDir)), 0.0), 3.0);
    col += uRim * fres * smoothstep(0.0, 0.7, lambert) * 0.16;

    gl_FragColor = vec4(col, 1.0);
  }
`;

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
const lerp = (a, b, t) => a + (b - a) * t;

function initHero(section) {
  if (section.dataset.lzReady === '1') return;
  section.dataset.lzReady = '1';

  const pin = section.querySelector('.lz-hero__pin');
  const stage = section.querySelector('.lz-hero__planet');
  const fallback = section.querySelector('.lz-hero__planet-fallback');
  const nebula = section.querySelector('.lz-hero__nebula');
  const stars = section.querySelector('.lz-hero__stars');
  const fade = section.querySelector('.lz-hero__fade');
  const cue = section.querySelector('.lz-hero__cue');
  const phrases = [...section.querySelectorAll('.lz-hero__phrase')];
  if (!pin || !stage) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const col = (name, fallbackHex) => {
    const v = (section.dataset[name] || '').trim();
    return new THREE.Color(v || fallbackHex);
  };

  /* ---- three.js scene ---- */
  let renderer, scene, camera, group, needsRender = true;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (e) {
    if (fallback) fallback.style.display = 'block';
    bindScroll(null);
    return;
  }

  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.domElement.style.cssText = 'position:absolute;inset:0;pointer-events:none;';
  stage.appendChild(renderer.domElement);
  if (fallback) fallback.style.display = 'none';

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
  camera.position.set(0, 0, 3.15);

  const uniforms = {
    uLightDir: { value: new THREE.Vector3(-0.62, 0.55, 0.58) },
    uShadow: { value: col('planetShadow', '#2c1220') },
    uBase: { value: col('planetBase', '#973b53') },
    uLight: { value: col('planetLight', '#d98a9b') },
    uRim: { value: col('planetRim', '#f0b6c4') },
  };

  group = new THREE.Group();
  group.rotation.set(0.32, 0.2, 0.06);
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(1, 128, 128),
    new THREE.ShaderMaterial({ vertexShader: planetVertex, fragmentShader: planetFragment, uniforms })
  );
  group.add(mesh);
  scene.add(group);

  function resize() {
    const r = stage.getBoundingClientRect();
    const size = Math.max(1, Math.min(r.width, r.height));
    renderer.setSize(size, size, false);
    renderer.domElement.style.width = r.width + 'px';
    renderer.domElement.style.height = r.height + 'px';
    camera.aspect = 1;
    camera.updateProjectionMatrix();
    needsRender = true;
  }

  function render() {
    if (needsRender) {
      renderer.render(scene, camera);
      needsRender = false;
    }
    raf = requestAnimationFrame(render);
  }

  /* ---- scroll → progress → animation ---- */
  function phraseOpacity(p, [a, b, c, d]) {
    if (p <= a || p >= d) return 0;
    if (p < b) return (p - a) / (b - a);
    if (p > c) return 1 - (p - c) / (d - c);
    return 1;
  }

  const RANGES = [
    [0.03, 0.12, 0.28, 0.37],
    [0.40, 0.49, 0.63, 0.72],
    [0.76, 0.85, 0.98, 1.0],
  ];

  function apply(progress) {
    const p = clamp(progress, 0, 1);

    if (group) {
      group.rotation.y = 0.2 + (reduced ? 0.3 : p * TURNS);
      const s = reduced ? SCALE_TO : lerp(SCALE_FROM, SCALE_TO, p);
      group.scale.setScalar(s);
      needsRender = true;
    }

    if (nebula) {
      nebula.style.transform = `scale(${lerp(1.02, 1.1, p)})`;
      nebula.style.opacity = p < 0.85 ? lerp(0.32, 0.4, p / 0.85) : lerp(0.4, 0, (p - 0.85) / 0.15);
    }
    if (stars) stars.style.opacity = p < 0.85 ? 0.88 : lerp(0.9, 0.15, (p - 0.85) / 0.15);
    if (fade) fade.style.opacity = p > 0.88 ? (p - 0.88) / 0.12 : 0;
    if (cue) cue.style.opacity = p < 0.08 ? 1 - p / 0.08 : 0;

    phrases.forEach((el, i) => {
      const range = RANGES[i] || RANGES[RANGES.length - 1];
      el.style.opacity = phraseOpacity(p, range);
      const h2 = el.firstElementChild;
      if (h2) {
        const t = clamp((p - range[0]) / (range[3] - range[0]), 0, 1);
        h2.style.transform = `translateY(${lerp(48, -48, t)}px)`;
      }
    });
  }

  function computeProgress() {
    const rect = section.getBoundingClientRect();
    const travel = rect.height - window.innerHeight;
    if (travel <= 0) return rect.top < 0 ? 1 : 0;
    return clamp(-rect.top / travel, 0, 1);
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      apply(computeProgress());
      ticking = false;
    });
  }

  function bindScroll() {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { resize(); apply(computeProgress()); });
    apply(computeProgress());
  }

  let raf;
  resize();
  render();
  bindScroll();

  if (reduced) {
    // one static frame, no scroll binding needed beyond the initial apply
    apply(0);
  }

  section._lzDestroy = () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('scroll', onScroll);
    renderer.dispose();
    mesh.geometry.dispose();
    mesh.material.dispose();
    if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    section.dataset.lzReady = '';
  };
}

function initAll() {
  document.querySelectorAll('.lozea-hero').forEach(initHero);
}

if (document.readyState !== 'loading') initAll();
else document.addEventListener('DOMContentLoaded', initAll);

/* Shopify theme editor: re-init when the section is re-rendered */
document.addEventListener('shopify:section:load', (e) => {
  const s = e.target.querySelector('.lozea-hero') || (e.target.classList?.contains('lozea-hero') ? e.target : null);
  if (s) initHero(s);
});
document.addEventListener('shopify:section:unload', (e) => {
  const s = e.target.querySelector('.lozea-hero');
  if (s && s._lzDestroy) s._lzDestroy();
});
